import { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { KibSparklesIcon } from '@chewy/kib-icons-react'
import classNames from 'classnames'

import type { AgentIngressOptions } from '~/components/agent/ingress/types'
import { AgentIngressPromptType } from '~/components/agent/ingress/types'
import type { PopperAction } from '~/components/common/ActionsPopper'
import RhapActionsPopper from '~/components/common/RhapActionsPopper'
import RhapTextButton from '~/components/common/RhapTextButton'
import { useAgentIngress } from '~/hooks/useAgentIngress'
import { useIsAgentChatEnabled } from '~/hooks/useIsAgentChatEnabled'

import '@chewy/kib-controls-styles/dist/styles/kib-button-new.css'
import styles from './AgentIngressDropdown.module.scss'

export interface AgentIngressDropdownProps extends AgentIngressOptions {
  /** Optional className for the trigger button */
  className?: string
}

/**
 * Dropdown component for agent ingress functionality.
 * Displays an icon-only button trigger (sparkles icon) with a dropdown menu containing ingress prompt options.
 *
 * @example
 * ```tsx
 * <AgentIngressDropdown
 *   patientId="123"
 *   clientId="456"
 * />
 * ```
 */
const AgentIngressDropdown = ({
  className,
  patientId,
  clientId,
}: AgentIngressDropdownProps) => {
  const { t } = useTranslation(['Agent'])
  const isAgentChatEnabled = useIsAgentChatEnabled()
  const { handleIngress } = useAgentIngress({ patientId, clientId })
  const buttonRef = useRef<HTMLButtonElement>(null)

  if (!isAgentChatEnabled) {
    return null
  }

  const actions: PopperAction[] = [
    {
      id: 'communication-history',
      label: t('Agent:COMMUNICATION_HISTORY'),
      onClick: () =>
        handleIngress(AgentIngressPromptType.COMMUNICATION_HISTORY),
    },
    {
      id: 'medical-history',
      label: t('Agent:MEDICAL_HISTORY'),
      onClick: () => handleIngress(AgentIngressPromptType.MEDICAL_HISTORY),
    },
    {
      id: 'overdue-items',
      label: t('Agent:OVERDUE_ITEMS'),
      onClick: () => handleIngress(AgentIngressPromptType.OVERDUE_ITEMS),
    },
  ]

  return (
    <>
      <RhapTextButton
        iconOnly
        aria-label={t('Agent:AI_ASSISTANT')}
        className={classNames(styles.triggerButton, className)}
        emphasis="primary"
        icon={<KibSparklesIcon height={20} width={20} />}
        ref={buttonRef}
        size="medium"
      />
      <RhapActionsPopper
        usePortal
        actions={actions}
        placement="bottom"
        title={t('Agent:SUMMARIZE')}
        trigger={buttonRef}
      />
    </>
  )
}

export default AgentIngressDropdown
